import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setChallenge(data.challenge_token);
      } else {
        await refresh();
        nav("/dashboard");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/verify-2fa", { challenge_token: challenge, code });
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight">
          {challenge ? "Two-factor verification" : "Welcome back"}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {challenge ? "Enter the 6-digit code from your authenticator app." : "Sign in to your ILFAL account."}
        </p>

        {err && (
          <Alert variant="destructive" className="mt-4" data-testid="login-error">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {!challenge ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="login-forgot-link">
                Forgot password?
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="code">6-digit code</Label>
              <Input id="code" data-testid="login-2fa-input" inputMode="numeric" maxLength={6} required value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg tracking-widest" />
            </div>
            <Button data-testid="login-2fa-submit-button" type="submit" className="w-full" disabled={loading}>
              {loading ? "Verifying…" : "Verify"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-sm text-slate-600">
          New to ILFAL? <Link to="/register" data-testid="login-register-link" className="text-blue-600 hover:underline">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
